import { Divider, Flex, HStack, Spinner, Stack } from '@chakra-ui/react';
import { useState } from 'react';
import { BsArrowUpCircle, BsArrowUpCircleFill } from 'react-icons/bs';
import { FaRegTrashAlt } from 'react-icons/fa';
import { MdPeople } from 'react-icons/md';
import {
  DashboardDocument,
  DtType,
  ProposalType,
  RequestsDocument,
  useDeleteRequestMutation,
  useUpvoteProposalMutation,
} from '../../generated/graphql';
import { getIndustryDetails } from '../../utils/industryDetails';
import { HelivanBackButton } from '../helivan/HelivanBackButton';
import { HelivanButton } from '../helivan/HelivanButton';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanLabel } from '../helivan/HelivanLabel';
import { HelivanText } from '../helivan/HelivanText';

interface TrendingRequestProps {
  title: string;
  id: string;
  type: ProposalType;
  why: string;
  dtType?: DtType;
  upvotes: number;
  youUpvoted: boolean;
  size: 'sm' | 'lg';
  showDelete?: boolean;
}
export const DashboardTrendingRequestCard = ({
  id,
  title,
  type,
  upvotes,
  why,
  youUpvoted,
  dtType,
  size,
  showDelete,
}: TrendingRequestProps) => {
  const [isDeleting, setIsDeleting] = useState(false);
  const [upvote, { loading }] = useUpvoteProposalMutation();
  const [deleteRequest, { loading: deleteRequestLoading }] =
    useDeleteRequestMutation();
  const details = getIndustryDetails(dtType ?? DtType.None);

  return (
    <Flex
      transition="0.2s"
      justifyContent="space-between"
      flexDirection="column"
      width={'full'}
      height={size === 'sm' ? '200px' : undefined}
      minHeight={size === 'lg' ? '280px' : undefined}
      px={size === 'sm' ? '18px' : '29px'}
      py={size === 'sm' ? '12px' : '20px'}
      borderRadius="9px"
      borderWidth="0.75px"
      borderColor={HelivanColor.border.primary}
      bg="white"
      _hover={{
        cursor: 'pointer',
        boxShadow: '0px 0px 6px 2px rgba(0, 0, 0, 0.15)',
      }}
      onClick={
        loading
          ? undefined
          : () => {
              upvote({
                variables: {
                  id: id,
                  isUpvote: !youUpvoted,
                },
                refetchQueries: [{ query: DashboardDocument }],
              });
            }
      }
    >
      {isDeleting ? (
        <Flex direction="column" height="full" justifyContent="space-between">
          <Stack
            spacing={4}
            alignItems="center"
            justifyContent="center"
            textAlign="center"
          >
            <HelivanText variant="sectionHeader">
              Would you like to delete the request:
            </HelivanText>
            <HelivanText variant="xlargeBody">{title}</HelivanText>
            <HelivanText variant="largeBody">"{why}"</HelivanText>
          </Stack>
          <Flex alignItems="center" justifyContent="space-between" width="full">
            <HelivanBackButton
              onClick={(e: any) => {
                e.stopPropagation();
                setIsDeleting(false);
              }}
            />
            <HelivanButton
              variant="action"
              onClick={() => {
                deleteRequest({
                  variables: {
                    id,
                  },
                  refetchQueries: [{ query: RequestsDocument }],
                });
              }}
            >
              {deleteRequestLoading ? (
                <Spinner size="xs" color="white" />
              ) : (
                'Confirm'
              )}
            </HelivanButton>
          </Flex>
        </Flex>
      ) : (
        <Flex
          justifyContent="space-between"
          flexDirection="column"
          height="full"
        >
          <Stack spacing={size === 'sm' ? 3 : 4}>
            <Stack spacing={1}>
              <HelivanText
                variant={size === 'sm' ? 'subheader' : 'sectionHeader'}
              >
                {title}
              </HelivanText>
              {dtType && dtType !== DtType.None && (
                <HStack spacing={1}>
                  <MdPeople
                    size={size === 'sm' ? 16 : 20}
                    color={HelivanColor.text.blueMid}
                  />
                  <HelivanText
                    variant={size === 'sm' ? 'smallBody+' : 'largeBody+'}
                  >
                    {details.industryDetails.title}
                  </HelivanText>
                </HStack>
              )}
            </Stack>
            <HelivanText variant={size === 'sm' ? 'smallBody' : 'largeBody'}>
              {why}
            </HelivanText>
          </Stack>
          <Stack spacing={size === 'sm' ? 3 : 4}>
            <Divider />

            <Flex
              width="full"
              justifyContent="space-between"
              alignItems="center"
            >
              <Flex
                justifyContent="space-between"
                borderRadius="15px"
                borderWidth="0.75px"
                borderColor={HelivanColor.border.primary}
                width="60px"
                pl="6px"
                pr="10px"
                py="5px"
                alignItems="center"
              >
                {loading ? (
                  <Flex
                    alignItems="center"
                    justifyContent="center"
                    width={size === 'sm' ? '20px' : '22px'}
                    height={size === 'sm' ? '20px' : '22px'}
                  >
                    <Spinner size="xs" />
                  </Flex>
                ) : youUpvoted ? (
                  <BsArrowUpCircleFill
                    size={size === 'sm' ? 20 : 22}
                    color={HelivanColor.text.blueDark}
                  />
                ) : (
                  <BsArrowUpCircle
                    size={size === 'sm' ? 20 : 22}
                    color={HelivanColor.text.blueDark}
                  />
                )}

                <HelivanText
                  variant={size === 'sm' ? 'smallBody+' : 'largeBody+'}
                >
                  {upvotes}
                </HelivanText>
              </Flex>
              <HStack spacing={1}>
                <HelivanLabel
                  variant={
                    type === ProposalType.Project
                      ? 'blue'
                      : type === ProposalType.DigitalTwin
                        ? 'green'
                        : 'success'
                  }
                  label={
                    type === ProposalType.Project
                      ? 'Project'
                      : type === ProposalType.DigitalTwin
                        ? 'Persona'
                        : 'Persona Collection'
                  }
                  size={size}
                />
              </HStack>
              {showDelete && (
                <Flex
                  color={HelivanColor.text.blueMid}
                  cursor="pointer"
                  onClick={(e: any) => {
                    e.stopPropagation();
                    setIsDeleting(true);
                  }}
                  width="32px"
                  height="32px"
                  alignItems="center"
                  justifyContent="center"
                  p={2}
                  borderRadius="8px"
                  _hover={{ bg: HelivanColor.background.lightestGrey }}
                >
                  <FaRegTrashAlt size={18} />
                </Flex>
              )}
            </Flex>
          </Stack>
        </Flex>
      )}
    </Flex>
  );
};
